import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdvisorFilterSortOptions } from '../components/Pages/Advisors/AdvisorResults/AdvisorsResults';
import { ApplicationRoutes } from '../constants';
import useUrlHelper from './UseUrlHelper';

interface UseNavigationService {
  navigateTo_Admin_EventsView: () => void;
  navigateTo_Admin_EventEdit: (eventId: string) => void;
  navigateTo_Admin_TheOngoingDetailsView: () => void;
  navigateTo_Admin_ThePlanDetailsView: () => void;
  navigateTo_Admin_IntakeResponse: () => void;
  navigateTo_Admin_IntakeResponseEdit: (intakeResponseId: string) => void;
  navigateTo_Admin_TheOngoingSignupEdit: (theOngoingSignupId?: string) => void;
  navigateTo_Admin_TheOngoingEdit: (theOngoingDetailId?: string) => void;
  navigateTo_Admin_ThePlanEdit: (thePlanDetailId?: string) => void;
  navigateTo_Admin_ThePlanSignupEdit: (thePlanSignupId?: string) => void;
  navigateTo_Admin_ToolsView: () => void;
  navigateTo_Admin_UsersView: () => void;
  navigateTo_Admin_UserEdit: (userId: string) => void;
  navigateTo_AdminDashboard: () => void;
  navigateTo_AdvisorsDashboard: () => void;
  navigateTo_AdvisorsDetails: (advisorId: string) => void;
  navigateTo_AdvisorsResults: (sort?: AdvisorFilterSortOptions, filters?: string[], price?: string) => void;
  navigateTo_Client_Dashboard: () => void;
  navigateTo_Client_Reviews: () => void;
  navigateTo_ComplianceReport: (clientId: string) => void;
  navigateTo_Home: () => void;
  navigateTo_Error: () => void;
  navigateTo_NotFound: () => void;
  navigateTo_TheOngoingTerms: (theOngoingDetailId: string) => void;
  navigateTo_TheOngoingPurchase: (theOngoingDetailId: string) => void;
  navigateTo_ThePlanTerms: (advisorId: string) => void;

  getUrl_Admin_EventEdit: (eventId: string) => string;
  getUrl_Admin_ThePlanEdit: (thePlanDetailId: string) => string;
  getUrl_Admin_TheOngoingEdit: (theOngoingDetailId: string) => string;
  getUrl_Admin_IntakeResponseEdit: (intakeResponseId: string) => string;
  getUrl_AdvisorsResults: (product: string) => string;
  getUrl_TheHourIntakeForm_Typeform: (firstName: string, email: string) => string;
  getUrl_TheOngoingSignup_Typeform: (userId: string, firstName: string, advisorTypeformChoiceId: string) => string;
  getUrl_ThePlanSignup_Typeform: (advisorTypeformChoiceId: string) => string;
}

const useNavigationService = (): UseNavigationService => {
  const navigate = useNavigate();

  const { getQueryStringParameter } = useUrlHelper();

  const getUrl_Admin_EventEdit = useCallback((eventId: string): string => {
    return `/${ApplicationRoutes.AdminRoutes.Events_Full}/${eventId}/edit`;
  }, []);

  const getUrl_Admin_UserEdit = useCallback((userId: string): string => {
    return `/${ApplicationRoutes.AdminRoutes.Users_Full}/${userId}/edit`;
  }, []);

  const getUrl_Admin_IntakeResponseEdit = useCallback((intakeResponseId?: string): string => {
    // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
    const reloadId = new Date().getTime();
    return `/${ApplicationRoutes.AdminRoutes.IntakeResponse_Edit_Full}/${reloadId}/${intakeResponseId ?? ''}`;
  }, []);

  const getUrl_AdvisorsResults = useCallback((product?: string): string => {
    return `/${ApplicationRoutes.Advisors}?price=${product}`;
  }, []);

  const getUrl_TheHourIntakeForm_Typeform = useCallback((firstName: string, email: string): string => {
    const params = [`email=${email}`, `name=${firstName}`].join('&');
    return `${ApplicationRoutes.IntakeForm}#${params}`;
  }, []);

  const getUrl_TheOngoingSignup_Typeform = useCallback((userId: string, firstName: string, advisorTypeformChoiceId: string): string => {
    const params = [`nuid=${userId}`, `name=${firstName}`, `answers-advisor=${advisorTypeformChoiceId}`].join('&');
    return `${ApplicationRoutes.TheOngoing_Signup}#${params}`;
  }, []);

  const getUrl_ThePlanSignup_Typeform = useCallback((advisorTypeformChoiceId: string): string => {
    return `${ApplicationRoutes.ThePlan_Signup}#answers-advisor=${advisorTypeformChoiceId}`;
  }, []);

  const getUrl_Admin_TheOngoingEdit = useCallback((theOngoingDetailId?: string): string => {
    // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
    const reloadId = new Date().getTime();
    return `/${ApplicationRoutes.AdminRoutes.TheOngoing_Edit_Full}/${reloadId}/${theOngoingDetailId ?? ''}`;
  }, []);

  const getUrl_Admin_ThePlanEdit = useCallback((thePlanDetailId?: string): string => {
    // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
    const reloadId = new Date().getTime();
    return `/${ApplicationRoutes.AdminRoutes.ThePlan_Edit_Full}/${reloadId}/${thePlanDetailId ?? ''}`;
  }, []);

  const navigateTo_AdvisorsResults = (sort?: AdvisorFilterSortOptions | null, filters?: string[], price?: string): void => {
    // Update the URL to reflect the selected attributes, like ?filters=real-estate+retirement
    sort = sort ?? getQueryStringParameter<AdvisorFilterSortOptions>('sort') ?? 'available';

    // If it's the default sort option, we don't show it in the URL
    if (sort === 'available') {
      sort = null;
    }

    let newUrl = '';
    const queryParts: string[] = [];

    if (filters?.length === 1) {
      newUrl += `/${filters[0]}`;
      if (sort) queryParts.push(`sort=${sort}`);
      if (price) queryParts.push(`price=${encodeURIComponent(price)}`);
      if (queryParts.length) newUrl += `?${queryParts.join('&')}`;
    } else if ((filters?.length ?? 0) > 0) {
      queryParts.push(`filters=${filters!.join('+')}`);
      if (sort) queryParts.push(`sort=${sort}`);
      if (price) queryParts.push(`price=${encodeURIComponent(price)}`);
      newUrl += `?${queryParts.join('&')}`;
    } else {
      if (sort) queryParts.push(`sort=${sort}`);
      if (price) queryParts.push(`price=${encodeURIComponent(price)}`);
      if (queryParts.length) newUrl += `?${queryParts.join('&')}`;
    }

    navigate(`/${ApplicationRoutes.Advisors}${newUrl}`);
  };

  const navigateTo_ComplianceReport = useCallback(
    (clientId: string): void => {
      navigate(`/${ApplicationRoutes.AdvisorAccountRoutes.Compliance_Full}/${clientId}/report`);
    },
    [navigate]
  );

  const navigateTo_AdvisorsDetails = useCallback(
    (advisorSlug: string): void => {
      navigate(`/${ApplicationRoutes.Advisor}/${advisorSlug}`);
    },
    [navigate]
  );

  const navigateTo_AdvisorsDashboard = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdvisorAccountRoutes.Root}`);
  }, [navigate]);

  const navigateTo_Client_Dashboard = useCallback((): void => {
    navigate(`/${ApplicationRoutes.ClientAccountRoutes.Root}`);
  }, [navigate]);

  const navigateTo_Client_Reviews = useCallback((): void => {
    navigate(`/${ApplicationRoutes.ClientAccountRoutes.Reviews_Full}`);
  }, [navigate]);

  const navigateTo_Home = useCallback((): void => {
    navigate(ApplicationRoutes.Root);
  }, [navigate]);

  const navigateTo_Error = useCallback((): void => {
    navigate(`/${ApplicationRoutes.Error}`);
  }, [navigate]);

  const navigateTo_NotFound = useCallback((): void => {
    navigate(`/${ApplicationRoutes.NotFound}`);
  }, [navigate]);

  const navigateTo_TheOngoingTerms = useCallback(
    (theOngoingDetailId: string): void => {
      navigate(`/${ApplicationRoutes.TheOngoing_Terms}/${theOngoingDetailId}`);
    },
    [navigate]
  );

  const navigateTo_TheOngoingPurchase = useCallback(
    (theOngoingDetailId: string): void => {
      navigate(`/${ApplicationRoutes.Purchase_TheOngoing}/${theOngoingDetailId}`);
    },
    [navigate]
  );

  const navigateTo_ThePlanTerms = useCallback(
    (advisorSlug: string): void => {
      navigate(`/${ApplicationRoutes.ThePlan_Terms}/${advisorSlug}`);
    },
    [navigate]
  );

  const navigateTo_Admin_EventEdit = useCallback(
    (eventId?: string): void => {
      navigate(getUrl_Admin_EventEdit(eventId ?? ''));
    },
    [getUrl_Admin_EventEdit, navigate]
  );

  const navigateTo_Admin_UserEdit = useCallback(
    (userId?: string): void => {
      navigate(getUrl_Admin_UserEdit(userId ?? ''));
    },
    [getUrl_Admin_UserEdit, navigate]
  );

  const navigateTo_Admin_TheOngoingEdit = useCallback(
    (theOngoingDetailId?: string): void => {
      // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
      navigate(getUrl_Admin_TheOngoingEdit(theOngoingDetailId));
    },
    [getUrl_Admin_TheOngoingEdit, navigate]
  );

  const navigateTo_Admin_ThePlanEdit = useCallback(
    (thePlanDetailId?: string): void => {
      // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
      navigate(getUrl_Admin_ThePlanEdit(thePlanDetailId));
    },
    [getUrl_Admin_ThePlanEdit, navigate]
  );

  const navigateTo_Admin_IntakeResponse = useCallback((): void => {
    // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
    navigate(`/${ApplicationRoutes.AdminRoutes.IntakeResponse_Full}`);
    return;
  }, [navigate]);

  const navigateTo_Admin_IntakeResponseEdit = useCallback(
    (intakeResponseId: string): void => {
      // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
      navigate(getUrl_Admin_IntakeResponseEdit(intakeResponseId));
    },
    [getUrl_Admin_IntakeResponseEdit, navigate]
  );

  const navigateTo_Admin_TheOngoingSignupEdit = useCallback(
    (theOngoingSignupId?: string): void => {
      // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
      const reloadId = new Date().getTime();
      navigate(`/${ApplicationRoutes.AdminRoutes.TheOngoing_Signup_Edit_Full}/${reloadId}/${theOngoingSignupId ?? ''}`);
    },
    [navigate]
  );

  const navigateTo_Admin_ThePlanSignupEdit = useCallback(
    (thePlanSignupId?: string): void => {
      // NOTE: We generate a reload ID timestamp so the page component knows when to reload from scratch
      const reloadId = new Date().getTime();
      navigate(`/${ApplicationRoutes.AdminRoutes.ThePlan_Signup_Edit_Full}/${reloadId}/${thePlanSignupId ?? ''}`);
    },
    [navigate]
  );

  const navigateTo_Admin_ThePlanDetailsView = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.ThePlan_Full}`);
  }, [navigate]);

  const navigateTo_Admin_TheOngoingDetailsView = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.TheOngoing_Full}`);
  }, [navigate]);

  const navigateTo_Admin_EventsView = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.Events_Full}`);
  }, [navigate]);

  const navigateTo_Admin_ToolsView = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.Tools_Full}`);
  }, [navigate]);

  const navigateTo_Admin_UsersView = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.Users_Full}`);
  }, [navigate]);

  const navigateTo_AdminDashboard = useCallback((): void => {
    navigate(`/${ApplicationRoutes.AdminRoutes.Root}`);
  }, [navigate]);

  return {
    navigateTo_Admin_EventsView,
    navigateTo_Admin_EventEdit,
    navigateTo_Admin_ThePlanDetailsView,
    navigateTo_Admin_TheOngoingDetailsView,
    navigateTo_Admin_TheOngoingEdit,
    navigateTo_Admin_ThePlanEdit,
    navigateTo_Admin_IntakeResponse,
    navigateTo_Admin_IntakeResponseEdit,
    navigateTo_Admin_TheOngoingSignupEdit,
    navigateTo_Admin_ThePlanSignupEdit,
    navigateTo_Admin_ToolsView,
    navigateTo_Admin_UserEdit,
    navigateTo_Admin_UsersView,
    navigateTo_AdminDashboard,
    navigateTo_AdvisorsDashboard,
    navigateTo_AdvisorsDetails,
    navigateTo_AdvisorsResults,
    navigateTo_Client_Dashboard,
    navigateTo_Client_Reviews,
    navigateTo_ComplianceReport,
    navigateTo_Error,
    navigateTo_NotFound,
    navigateTo_Home,
    navigateTo_TheOngoingTerms,
    navigateTo_TheOngoingPurchase,
    navigateTo_ThePlanTerms,
    getUrl_Admin_EventEdit,
    getUrl_Admin_ThePlanEdit,
    getUrl_Admin_TheOngoingEdit,
    getUrl_Admin_IntakeResponseEdit,
    getUrl_AdvisorsResults,
    getUrl_TheHourIntakeForm_Typeform,
    getUrl_TheOngoingSignup_Typeform,
    getUrl_ThePlanSignup_Typeform
  };
};

export default useNavigationService;
